import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { adminAuthCheck, adminNumbeoList } from "../lib/api";
import { useT } from "../i18n/I18nProvider";
import DataErrorNotice from "../components/DataErrorNotice";
import { Lock, AlertCircle, LogOut, RefreshCw, Pencil, BadgeCheck, ExternalLink, Database } from "lucide-react";
import { formatDateFr } from "../lib/format";

const STORAGE_KEY = "hejpoland_admin_secret";

function pctBadge(pct) {
  if (pct === null || pct === undefined) return { cls: "bg-slate-100 text-slate-500", txt: "—" };
  if (pct >= 95) return { cls: "bg-emerald-50 text-emerald-700 border border-emerald-200", txt: `${pct.toFixed(1)}%` };
  if (pct >= 80) return { cls: "bg-amber-50 text-amber-700 border border-amber-200",     txt: `${pct.toFixed(1)}%` };
  return                 { cls: "bg-red-50 text-red-700 border border-red-200",          txt: `${pct.toFixed(1)}%` };
}

function dateBadge(isoDate) {
  if (!isoDate) return { cls: "bg-slate-100 text-slate-500", days: null };
  const d = new Date(isoDate);
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days < 30) return  { cls: "bg-emerald-50 text-emerald-700 border border-emerald-200", days };
  if (days < 90) return  { cls: "bg-amber-50 text-amber-700 border border-amber-200",       days };
  return                 { cls: "bg-red-50 text-red-700 border border-red-200",             days };
}

export default function AdminNumbeo() {
  const { t, lp } = useT();
  const [secret, setSecret] = useState(() => sessionStorage.getItem(STORAGE_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [pwInput, setPwInput] = useState("");
  const [authError, setAuthError] = useState("");
  const [rows, setRows] = useState([]);
  const [loadErr, setLoadErr] = useState(false);

  useEffect(() => {
    if (!secret) return;
    adminAuthCheck(secret).then(() => setAuthed(true)).catch(() => {
      sessionStorage.removeItem(STORAGE_KEY); setSecret("");
    });
  }, []); // eslint-disable-line

  const reload = useCallback(async () => {
    if (!authed) return;
    setLoadErr(false);
    try { setRows(await adminNumbeoList(secret)); }
    catch (e) {
      console.error("[AdminNumbeo] load failed:", e?.message);
      setLoadErr(true);
    }
  }, [authed, secret]);

  useEffect(() => { reload(); }, [reload]);

  const login = async (e) => {
    e.preventDefault();
    setAuthError("");
    try {
      await adminAuthCheck(pwInput);
      sessionStorage.setItem(STORAGE_KEY, pwInput);
      setSecret(pwInput); setAuthed(true); setPwInput("");
    } catch { setAuthError(t("adminSources.invalidSecret")); }
  };

  const logout = () => { sessionStorage.removeItem(STORAGE_KEY); setSecret(""); setAuthed(false); };

  if (!authed) {
    return (
      <div className="max-w-md mx-auto px-4 py-24" data-testid="admin-numbeo-login">
        <div className="bg-white border border-slate-200 rounded-2xl p-8">
          <div className="w-12 h-12 rounded-2xl bg-[#0056D2]/10 flex items-center justify-center mb-5">
            <Lock className="w-5 h-5 text-[#0056D2]" />
          </div>
          <h1 className="font-display text-2xl font-semibold text-slate-900">{t("adminNumbeo.title")}</h1>
          <p className="text-sm text-slate-600 mt-2 mb-6">{t("adminSources.needSecret")}.</p>
          <form onSubmit={login} className="space-y-3">
            <input type="password" autoFocus value={pwInput} onChange={(e) => setPwInput(e.target.value)} placeholder={t("adminSources.promptSecret")} className="w-full px-4 py-2.5 border border-slate-200 rounded-xl bg-slate-50 focus:bg-white focus:ring-2 focus:ring-[#0056D2] focus:border-transparent outline-none" data-testid="admin-numbeo-secret-input" />
            {authError && <div className="flex items-center gap-2 text-sm text-[#DC143C]"><AlertCircle className="w-4 h-4" />{authError}</div>}
            <button type="submit" className="w-full bg-[#0056D2] hover:bg-[#0047AD] text-white font-medium py-2.5 rounded-full transition" data-testid="admin-numbeo-login-submit">Se connecter</button>
          </form>
        </div>
      </div>
    );
  }

  // Global last-update banner
  const latestIso = rows.reduce((max, r) => (!r.collected_at ? max : (!max || r.collected_at > max ? r.collected_at : max)), null);
  const latestDays = latestIso ? Math.floor((Date.now() - new Date(latestIso).getTime()) / 86400000) : null;

  return (
    <div className="max-w-6xl mx-auto px-4 py-12 md:py-16" data-testid="admin-numbeo-page">
      <header className="flex items-start justify-between flex-wrap gap-4 mb-8">
        <div>
          <span className="hej-chip"><span className="w-1.5 h-1.5 rounded-full bg-[#DC143C]"></span>Admin · Numbeo</span>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mt-3 flex items-center gap-2">
            <Database className="w-7 h-7 text-[#0056D2]" /> {t("adminNumbeo.title")}
          </h1>
          <p className="text-slate-600 mt-2 text-sm max-w-3xl">{t("adminNumbeo.intro")}</p>
          <p className="text-xs text-slate-500 mt-2" data-testid="admin-numbeo-global-update">
            {latestDays !== null ? t("adminNumbeo.lastUpdateGlobal", { n: latestDays }) : t("adminNumbeo.lastUpdateNever")}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link to={lp("/admin/sources")} className="px-4 py-2 rounded-full bg-white border border-slate-200 hover:bg-slate-50 text-sm">Sources</Link>
          <Link to={lp("/admin/data")} className="px-4 py-2 rounded-full bg-white border border-slate-200 hover:bg-slate-50 text-sm">Données seed</Link>
          <button onClick={reload} className="px-4 py-2 rounded-full bg-white border border-slate-200 hover:bg-slate-50 text-sm flex items-center gap-1.5" data-testid="admin-numbeo-reload"><RefreshCw className="w-3.5 h-3.5" /> Recharger</button>
          <button onClick={logout} className="px-4 py-2 rounded-full bg-slate-100 hover:bg-slate-200 text-sm flex items-center gap-1.5"><LogOut className="w-3.5 h-3.5" /> {t("adminSources.logout")}</button>
        </div>
      </header>

      {loadErr && <div className="mb-6"><DataErrorNotice onRetry={reload} /></div>}

      <h2 className="font-display text-lg font-semibold text-slate-900 mb-4">{t("adminNumbeo.listHeading")}</h2>

      <div className="overflow-x-auto rounded-2xl border border-slate-200" data-testid="admin-numbeo-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="px-4 py-3 text-left font-semibold">{t("adminNumbeo.colCity")}</th>
              <th className="px-4 py-3 text-left font-semibold">{t("adminNumbeo.colLastUpdate")}</th>
              <th className="px-4 py-3 text-left font-semibold">{t("adminNumbeo.colCompleteness")}</th>
              <th className="px-4 py-3 text-right font-semibold">{t("adminNumbeo.colContributors")}</th>
              <th className="px-4 py-3 text-right font-semibold">{t("adminNumbeo.colEntries")}</th>
              <th className="px-4 py-3 text-right font-semibold">{t("adminNumbeo.colAction")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const pBadge = pctBadge(r.completeness_pct);
              const dBadge = dateBadge(r.numbeo_last_update);
              return (
                <tr key={r.slug} className="border-t border-slate-100 hover:bg-slate-50/50" data-testid={`admin-numbeo-row-${r.slug}`}>
                  <td className="px-4 py-3 font-medium text-slate-900">
                    <div className="flex items-center gap-2">
                      <BadgeCheck className="w-3.5 h-3.5 text-emerald-600" />
                      <span>{r.name_fr}</span>
                      {r.numbeo_url && (
                        <a href={r.numbeo_url} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-[#0056D2]" title="Ouvrir Numbeo">
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                    <div className="text-xs text-slate-400 mt-0.5">{r.slug}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${dBadge.cls}`}>
                      {formatDateFr(r.numbeo_last_update)}
                      {dBadge.days !== null && <span className="text-[10px] opacity-75">({dBadge.days}j)</span>}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${pBadge.cls}`} data-testid={`pct-${r.slug}`}>
                      {pBadge.txt}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-slate-600">{r.contributors_12m ?? "—"}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-slate-600">{r.entries_12m ?? "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <Link
                      to={lp(`/admin/numbeo/${r.slug}`)}
                      className="inline-flex items-center gap-1.5 text-xs font-medium bg-[#0056D2] hover:bg-[#0047AD] text-white px-3 py-1.5 rounded-full transition"
                      data-testid={`edit-${r.slug}`}
                    >
                      <Pencil className="w-3 h-3" /> {t("adminNumbeo.edit")}
                    </Link>
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">—</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
